// src/db/deskCache.js — the Desk's stale-while-revalidate cache, and its EVICTION POLICY.
//
// Split out of db/core.js on 2026-08-09 for one reason: core.js imports db/index.js, which
// pulls in the supabase client, so none of this could be tested under `node --test`. The
// eviction rules below are exactly the kind of logic that needs a test, and they had none.
// core.js re-exports both functions, so no caller changed.
//
// Fast first paint only, NEVER a source of truth: callers render the cached copy immediately,
// then always refetch and overwrite. Saved listings are immutable (changes arrive as NEW rows
// / explicit UI edits / self-describing update notifications), so a briefly stale feed is safe
// — the background revalidate is what keeps it honest. Bump CACHE_VER whenever a cached
// payload's shape changes.
// v2 (2026-07-15): the prefetch-to-complete chain was removed (egress diet). v1 caches hold
// full 2000-row snapshots whose dead rows were purged only when the chain COMPLETED — with the
// chain gone that purge never runs for large feeds, so server-deleted rows in an old cached
// tail would linger on screen forever. Bumping evicts them all.
export const CACHE_VER = 'v2'
export const CACHE_PREFIX = `sntv_desk_${CACHE_VER}:`

// ── NOT EVERY DESK CACHE IS WORTH THE SAME (2026-08-09) ──────────────────────
// Owner report: "whenever I switch around this needs to recalculate too long."
//
// Twelve writers share this one bucket, and they are wildly different sizes. Three of them are
// TINY and are precisely what makes a switch feel instant:
//   tc:  ONE integer (the corpus total)
//   fc:  two small maps (kind + intent counts)
//   dc2: the district count map (~100 keys)
// The rest are whole feed pages (`al:`/`alf:`, ~100 listings with full card payloads), map
// pins, and proposal sets — hundreds of KB each.
//
// The old handler, on ANY quota failure, deleted EVERY `sntv_desk_*` key and retried. Two
// things made that worse than no cache at all:
//   1. Every pool / scope / period / type switch mints a NEW key rather than replacing one, so
//      the bucket only grows as an agent moves around. Quota is reached by NORMAL USE.
//   2. The wipe ran on the write that FAILED, then retried it — so the oversized feed page that
//      blew the quota got written and SURVIVED, while the three tiny count caches were deleted.
//      Exactly backwards: the biggest, least reusable entry won, and the caches whose entire job
//      is instant switching were the collateral.
// Net effect: switch a few times, the cache wipes itself, and the next switch pays a full corpus
// recount — the 850ms-4.2s query measured in migration 0518's header.
//
// So eviction is ORDERED and PROTECTED rather than total:
//   - PROTECTED keys are never swept. The whole set is a few hundred bytes, so it can never
//     itself cause a quota failure. Keeping a stale count map across a wipe is the SAME
//     contract these caches already declare ("stale-true beats fresh-false" — see the dc2/tc
//     notes in IntakeTab).
//   - Everything else is evicted OLDEST-FIRST, one at a time, retrying after each, so a single
//     large write costs one or two old pages instead of the entire cache.
//   - A payload too big to ever be worth a page slot is refused OUTRIGHT rather than admitted
//     by evicting everything else to make room for it.
// ⚠️ If you add a new cache key prefix, decide which side of PROTECTED it is on. A big payload
// added to the protected set re-creates the exact bug this replaces.
export const PROTECTED_PREFIXES = ['tc:', 'fc:', 'dc2:', 'lfc:']   // lfc: F-067, a facet's last unconstrained answer (small maps)
// 2026-09-24: `pw:` (the counts prewarm's whole answer, kept 15 min so a remount replays it instead
// of re-running a full search) is deliberately NOT protected — with the street map it can reach
// tens of KB, and losing it costs only the warm the Desk used to run on every mount anyway.
// Freshness for these keys rides INSIDE the payload (`at` / `__at`), never in the stamp sidecar
// below, which is a write SEQUENCE for eviction order and knows nothing about time.
// Above this, a single entry is not worth what it would evict. 512 KB is deliberately generous
// for a ~100-row page and still an order of magnitude under the ~5 MB origin quota, so the
// refusal only fires on genuinely outsized payloads (the corpus pin blob).
export const MAX_ENTRY_BYTES = 512 * 1024

// Resolved at CALL time, never captured at import time — tests install a stub, and a module
// that grabbed the reference on load would hold the wrong one.
const ls = () => globalThis.localStorage

const isProtected = full => PROTECTED_PREFIXES.some(p => full.startsWith(CACHE_PREFIX + p))

// Oldest-first by write order. The stamp lives in a sidecar map rather than inside each payload
// so entries keep their existing shape (no CACHE_VER bump needed).
const STAMP_KEY = `${CACHE_PREFIX}__stamps`
function stamps() {
    try { return JSON.parse(ls().getItem(STAMP_KEY) || '{}') } catch { return {} }
}
// Monotonic counter, NOT Date.now(): two writes in the same millisecond must still order, and a
// clock change must not reshuffle the eviction queue.
let writeSeq = 0

export function readDeskCache(key) {
    try {
        const raw = ls().getItem(CACHE_PREFIX + key)
        return raw ? JSON.parse(raw) : null
    } catch { return null }
}

export function writeDeskCache(key, data) {
    const full = CACHE_PREFIX + key
    let body
    try { body = JSON.stringify(data) } catch { return }   // unserializable → simply not cached
    if (body === undefined) return
    // Refuse the outsized rather than evict the useful to make room for it.
    if (body.length > MAX_ENTRY_BYTES && !isProtected(full)) return

    const seq = ++writeSeq
    const write = () => {
        ls().setItem(full, body)
        // The stamp is an optimisation: losing it degrades eviction to "unstamped first",
        // which is still a defined order. It must never fail the write it belongs to.
        try {
            const s = stamps(); s[full] = seq
            ls().setItem(STAMP_KEY, JSON.stringify(s))
        } catch { /* ignore */ }
    }
    try { write(); return } catch { /* fall through to ordered eviction */ }

    const s = stamps()
    const victims = Object.keys(ls())
        .filter(k => k.startsWith('sntv_desk_') && k !== STAMP_KEY && k !== full && !isProtected(k))
        .sort((a, b) => (s[a] ?? -1) - (s[b] ?? -1))   // unstamped (pre-upgrade) entries go first
    for (const victim of victims) {
        try { ls().removeItem(victim) } catch { /* ignore and keep going */ }
        try { write(); return } catch { /* still short — evict the next oldest */ }
    }
    // Nothing evictable left. Rather than sacrifice the protected counts — the whole point of
    // this module — accept that this one payload is not cached.
}
